import { localStorageColorSchemeManager, MantineProvider } from '@mantine/core'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { navigate } from 'gatsby'
import React, { FC, ReactNode, useEffect, useState } from 'react'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Auth0Provider } from '@auth0/auth0-react'
import { cssVariablesResolver, theme } from '../theme'
import { MantineEmotionProvider } from '@mantine/emotion'
import { DatesProvider } from '@mantine/dates'
import { useTranslation } from 'react-i18next'

import 'dayjs/locale/nl'
import 'dayjs/locale/en'
import 'dayjs/locale/de'
import dayjs from 'dayjs'
import { ModalsProvider } from '@mantine/modals'

// const onRedirectCallback = (appState: any) => {
//   navigate(
//     appState && appState.returnTo
//       ? appState.returnTo
//       : window.location.pathname,
//     { replace: true },
//   )
// }

const onRedirectCallback = (appState: any) =>
  navigate(appState?.returnTo || `/`)

const audience = process.env.GATSBY_AUTH0_AUDIENCE

export const Providers: FC<any> = ({ children }: { children: ReactNode }) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      // Don't let React Query pause on `navigator.onLine`: Chrome can report `false` while the
      // network works fine, which leaves every query paused without a request or an error.
      // `offlineFirst` isn't enough: it pauses retries until the browser reports online again,
      // which in that state never happens.
      queries: {
        networkMode: `always`,
        // Retry only when the request couldn't reach the server (fetch throws a TypeError in
        // every browser), never on HTTP or login errors. A longer outage shows the page's error
        // state, and React Query refetches on the browser's `online` event.
        retry: (failureCount, error) =>
          error instanceof TypeError && failureCount < 3,
      },
      mutations: {
        // No retries: if the connection dropped after the server saved a change, a retry would
        // save it twice
        networkMode: `always`,
      },
    },
  })

  const { i18n } = useTranslation()

  useEffect(() => {
    dayjs.locale(i18n.language.slice(0, 2))
  }, [i18n.language])

  const colorSchemeManager = localStorageColorSchemeManager({
    key: 'krane-dashboard-color-scheme',
  })

  return (
    <Auth0Provider
      domain={process.env.GATSBY_AUTH0_DOMAIN || ``}
      clientId={process.env.GATSBY_AUTH0_CLIENTID || ``}
      authorizationParams={{
        redirect_uri: process.env.GATSBY_AUTH0_CALLBACK || ``,
        audience,
      }}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens
      useRefreshTokensFallback
    >
      <MantineEmotionProvider>
        <MantineProvider
          theme={theme}
          colorSchemeManager={colorSchemeManager}
          cssVariablesResolver={cssVariablesResolver}
        >
          <ModalsProvider>
            <QueryClientProvider client={queryClient}>
              {/* Bottom left would cover the navbar's collapse button */}
              <ReactQueryDevtools
                initialIsOpen={false}
                position="bottom-right"
              />

              <DatesProvider
                settings={{
                  locale: (i18n.language || `en`).slice(0, 2),
                }}
              >
                {children}
              </DatesProvider>
            </QueryClientProvider>
          </ModalsProvider>
        </MantineProvider>
      </MantineEmotionProvider>
    </Auth0Provider>
  )
}
